import { useEffect, useState } from "react";
import { Loader2, NotebookPen, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api } from "../lib/api";
import { toast } from "sonner";

const COLOR_DOTS = {
  amber: "bg-amber-400",
  rose: "bg-rose-400",
  sky: "bg-sky-400",
  emerald: "bg-emerald-400",
  violet: "bg-violet-400",
  slate: "bg-slate-400",
};

/**
 * Quick-save modal — "הכנס למחברת".
 * Renders the captured content (read-only), lets the user pick a section
 * and add a personal note, then POSTs to /api/notebook/entries.
 *
 * Two modes:
 *   • Single source: pass `payload` (one NotebookEntryCreate object)
 *   • Bulk save (e.g. lesson basket): pass `bulkPayloads` — an array of
 *     entry payloads. We save them all under the same section, sharing
 *     the user's note across each, in one click.
 *
 * Props:
 *   open       — bool
 *   onClose    — () => void
 *   payload    — single payload (omit if bulkPayloads given)
 *   bulkPayloads — array of payloads (omit if payload given)
 *   bulkLabel    — header label, e.g. "שמור 5 פסוקים מהסל"
 */
export default function SaveToNotebookModal({ open, onClose, payload, bulkPayloads, bulkLabel }) {
  const isBulk = Array.isArray(bulkPayloads) && bulkPayloads.length > 0;
  const [sections, setSections] = useState([]);
  const [sectionId, setSectionId] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load sections + auto-pick a sensible default based on source_type.
  useEffect(() => {
    if (!open) return;
    setNote("");
    setLoading(true);
    api
      .notebookSections()
      .then((d) => {
        const items = d?.items || [];
        setSections(items);
        const probeType = isBulk ? bulkPayloads[0]?.source_type : payload?.source_type;
        const defaultMap = {
          verse: "פסוקים אהובים",
          daily: "פסוקים אהובים",
          ai: "רעיונות",
        };
        const wantTitle = defaultMap[probeType] || "כללי";
        const match =
          items.find((s) => s.title === wantTitle) || items[0];
        setSectionId(match?.id || null);
      })
      .catch(() => toast.error("שגיאה בטעינת חוצצי המחברת"))
      .finally(() => setLoading(false));
  }, [open, payload?.source_type, isBulk, bulkPayloads]);

  const handleSave = async (e) => {
    // The modal's Save button can be rendered (via Radix Portal) on top of
    // a parent <Link> or other clickable card. After the modal closes, the
    // synthetic click event has been observed to reach the underlying
    // anchor and trigger a stray navigation. Cancel both behaviours on
    // every save click — pure defence in depth.
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!sectionId) return;
    setSaving(true);
    try {
      if (isBulk) {
        // Merge all sources into ONE entry — that's why the user picked
        // multiple together. Each source becomes a clickable
        // `notebook-verse-card` inside the merged `user_note`, so every
        // verse in the saved entry is navigable on its own (as opposed
        // to the older blob-of-text rendering where the user couldn't
        // click through to any specific verse).
        const refs = [];
        const cards = [];
        for (const p of bulkPayloads) {
          if (p.source_ref_he) refs.push(p.source_ref_he);
          // Build a verse-card-shaped div per source. The class +
          // dataset shape match the editor's verse-card so the
          // existing onNoteClick handler routes the user to /reader.
          const dataAttrs = [
            p.source_book && `data-book="${escAttr(p.source_book)}"`,
            p.source_chapter != null && `data-ch="${escAttr(String(p.source_chapter))}"`,
            p.source_verse != null && `data-v="${escAttr(String(p.source_verse))}"`,
            p.source_ref_he && `data-ref="${escAttr(p.source_ref_he)}"`,
          ].filter(Boolean).join(" ");
          const headerParts = [p.source_label, p.source_ref_he].filter(Boolean).join(" · ");
          const heText = esc(p.content_he || "");
          const enText = esc(p.content_text || "");
          // Use `notebook-source-block` instead of `notebook-verse-card`
          // because the user did NOT manually insert these via the
          // verse-link tool — they're "imported saved sources". The
          // class is recognized by EntryCard.onNoteClick for nav, and
          // it has no close-X (which only makes sense inside the editor).
          cards.push(
            `<div class="notebook-source-block" contenteditable="false" ${dataAttrs}>` +
              (headerParts ? `<div class="notebook-source-block-ref">${esc(headerParts)}</div>` : "") +
              (heText ? `<div class="notebook-source-block-he" dir="rtl">${heText}</div>` : "") +
              (enText ? `<div class="notebook-source-block-en" dir="ltr">${enText}</div>` : "") +
            `</div>`
          );
        }

        // Range label: "בראשית א:א – ויקרא ב:ה" if first/last differ,
        // otherwise just the single ref. Falls back to "{n} מקורות".
        let mergedRef = `${bulkPayloads.length} מקורות`;
        if (refs.length === 1) mergedRef = refs[0];
        else if (refs.length >= 2) mergedRef = `${refs[0]} – ${refs[refs.length - 1]}`;

        // First payload's book/chapter/verse drive the source link so the
        // user can click "מקור" on the saved entry and jump to chapter 1.
        const first = bulkPayloads[0] || {};

        // The merged user_note: every source as a clickable card,
        // followed by the user's personal note (if any). We render the
        // personal note in its own block so it never gets visually
        // confused with the captured-source content.
        const noteBlock = note
          ? `<div class="notebook-bulk-note"><p>${esc(note).replace(/\n/g, "<br>")}</p></div>`
          : "";
        const mergedNote = cards.join("") + noteBlock;

        await api.notebookCreateEntry({
          source_type: "freeform",
          section_id: sectionId,
          source_book: first.source_book,
          source_chapter: first.source_chapter,
          source_verse: first.source_verse,
          source_ref_he: mergedRef,
          source_label: `אוסף · ${bulkPayloads.length} מקורות`,
          title: bulkLabel || mergedRef,
          // Intentionally NOT setting content_he/content_text — the
          // visible body now lives entirely in user_note as
          // per-source clickable cards. Keeping the legacy fields empty
          // also means the editor can re-open this entry cleanly
          // without the migration helper needing to do anything.
          user_note: mergedNote || undefined,
        });
        toast.success("המקורות נשמרו כרשומה אחת ✨", {
          description: mergedRef,
        });
      } else {
        await api.notebookCreateEntry({
          ...payload,
          section_id: sectionId,
          user_note: note || undefined,
        });
        toast.success("נשמר במחברת ✨", {
          description: payload?.source_ref_he || "רשומה חדשה",
        });
      }
      onClose?.();
    } catch (err) {
      toast.error("שגיאה בשמירה", {
        description: err.response?.data?.detail || "נסה שוב",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose?.()}>
      <DialogContent
        className="max-w-lg"
        dir="rtl"
        data-testid="save-to-notebook-modal"
        // Belt-and-braces: when this modal is summoned from inside a
        // clickable card (Landing parasha/daily verse), any click that
        // accidentally escapes the portal could trigger the card's <Link>.
        // Catch every click on the container and stop it cold.
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2 text-foreground">
            <NotebookPen className="h-5 w-5 text-gold" strokeWidth={1.5} />
            הכנס למחברת
          </DialogTitle>
          <DialogDescription className="text-right text-xs text-muted-foreground">
            שמור את התוכן הזה במחברת האישית שלך עם הערה משלך.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="grid place-items-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-gold" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Captured content preview */}
            {isBulk ? (
              <div className="rounded-sm border border-border/60 bg-card/40 p-3 max-h-48 overflow-y-auto">
                <p className="text-[11px] text-gold uppercase tracking-wider mb-2 font-heading">
                  {bulkLabel || `${bulkPayloads.length} מקורות יישמרו ביחד כרשומה אחת`}
                </p>
                <ul className="space-y-1.5 text-sm">
                  {bulkPayloads.slice(0, 8).map((p, i) => (
                    <li key={i} className="text-foreground/85 truncate">
                      <span className="text-muted-foreground text-xs ms-2">·</span>
                      {p.source_label && <span className="text-gold/80 ms-1">{p.source_label}</span>}
                      {p.source_ref_he || p.title || (p.content_he || p.content_text || "").slice(0, 40)}
                    </li>
                  ))}
                  {bulkPayloads.length > 8 && (
                    <li className="text-xs text-muted-foreground">
                      …ועוד {bulkPayloads.length - 8} מקורות
                    </li>
                  )}
                </ul>
                <p className="mt-2 pt-2 border-t border-border/40 text-[11px] text-muted-foreground italic">
                  ✨ ייווצר רשומה אחת מאוחדת עם כל המקורות והערה משותפת
                </p>
              </div>
            ) : (
              <div className="rounded-sm border border-border/60 bg-card/40 p-3 max-h-40 overflow-y-auto">
                {payload?.source_ref_he && (
                  <p className="text-[11px] text-gold uppercase tracking-wider mb-1.5 font-heading">
                    {payload.source_label
                      ? `${payload.source_label} · ${payload.source_ref_he}`
                      : payload.source_ref_he}
                  </p>
                )}
                {payload?.title && (
                  <p className="font-heading text-sm text-foreground mb-1">
                    {payload.title}
                  </p>
                )}
                {payload?.content_he && (
                  <p className="font-verse text-sm text-foreground/90 leading-loose" dir="rtl">
                    {payload.content_he}
                  </p>
                )}
                {!payload?.content_he && payload?.content_text && (
                  <p className="text-sm text-foreground/90 leading-relaxed">
                    {payload.content_text.slice(0, 600)}
                    {payload.content_text.length > 600 ? "…" : ""}
                  </p>
                )}
              </div>
            )}

            {/* Section picker — colored chips */}
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-2 font-heading">
                בחר חוצץ
              </label>
              <div className="flex flex-wrap gap-2" data-testid="notebook-section-picker">
                {sections.map((s) => {
                  const active = s.id === sectionId;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setSectionId(s.id)}
                      data-testid={`section-pick-${s.id}`}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border text-sm transition-colors ${
                        active
                          ? "border-gold bg-gold/10 text-foreground"
                          : "border-border/60 hover:border-gold/40 text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <span className={`h-2 w-2 rounded-full ${COLOR_DOTS[s.color] || "bg-amber-400"}`} />
                      {s.title}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Note */}
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-2 font-heading">
                הערה אישית (אופציונלי)
              </label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="מה למדת? איזו שאלה זה מעורר? כל מה שתרצה לזכור..."
                rows={4}
                dir="rtl"
                data-testid="notebook-entry-note"
                className="resize-none"
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-2">
              <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
                <Sparkles className="h-3 w-3 text-gold" strokeWidth={1.5} />
                המחברת שלך, שלך לחיים.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={onClose}
                  disabled={saving}
                  data-testid="notebook-save-cancel"
                >
                  ביטול
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!sectionId || saving}
                  className="sacred-btn"
                  data-testid="notebook-save-confirm"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin ms-2" />
                  ) : (
                    <NotebookPen className="h-4 w-4 ms-2" strokeWidth={1.5} />
                  )}
                  שמור במחברת
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// HTML-escape helpers for the bulk save path. content_he comes from
// Sefaria so it's trusted text, but it can still contain raw < / & / "
// characters that would break the generated markup. Keep these tight
// and side-effect free.
function esc(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
function escAttr(s) {
  return String(s).replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

